import * as B from '@blueprintjs/core';
import classNames from 'classnames';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';
import {isNoteDismissed, isNoteSaved} from 'app/pages/Agent/utils';
import {makeAreaDisplayString} from 'app/utils/geoJsonUtils';
import {makeNoteUrlFragment, makeProjectDashboardUrl} from 'app/utils/routeUtils';

import {SelectedNoteReview} from './ReviewObservations';
import cs from './styles.module.scss';
import {ApiAgentNote} from './types';

interface NotesQueueSidebarProps {
  noteQueue: ApiAgentNote[];
  currentIndex: number;
  onNoteSelect: (index: number) => void;
  features: ApiFeature[];
  currentNoteRef?: React.RefObject<HTMLDivElement>;
  setNoteWithEdits: (note: ApiAgentNote) => void;
  onDismiss: (noteWithEdits: ApiAgentNote) => void;
  onSave: (noteWithEdits: ApiAgentNote) => void;
  noteWithEdits: ApiAgentNote;
  areaUnit: ApiOrganizationAreaUnit;
  selectedNoteIds: ReadonlySet<string>;
  toggleNoteSelected: (noteId: string | number) => void;
}

export const NoteQueue: React.FC<NotesQueueSidebarProps> = ({
  noteQueue,
  currentIndex,
  onNoteSelect,
  features,
  currentNoteRef,
  setNoteWithEdits,
  onDismiss,
  onSave,
  noteWithEdits,
  areaUnit,
  selectedNoteIds,
  toggleNoteSelected,
}: NotesQueueSidebarProps) => {
  // Areas come from the note geometries, so they only move when the queue itself does.
  const noteAreasById = React.useMemo(() => {
    const areas = new Map<string, string | null>();
    for (const note of noteQueue) {
      areas.set(String(note.id), makeAreaDisplayString(note.geometry ?? null, areaUnit));
    }
    return areas;
  }, [noteQueue, areaUnit]);

  return (
    <>
      <div>
        <div className={cs.sidebarContent}>
          <div className={cs.sidebarNotesList}>
            {noteQueue.map((note, index) => {
              const isCurrent = index === currentIndex;
              const noteArea = noteAreasById.get(String(note.id));

              return (
                <div
                  key={index}
                  ref={isCurrent ? currentNoteRef : undefined}
                  className={classNames(cs.sidebarNoteItem, {
                    [cs.sidebarNoteCurrent]: isCurrent,
                    [cs.sidebarNoteDismissed]:
                      (isNoteDismissed(note) || isNoteSaved(note)) && !isCurrent,
                  })}
                  onClick={() => onNoteSelect(index)}
                >
                  <div className={cs.sidebarNoteHeader}>
                    <div className={cs.sidebarNoteHeaderLeft}>
                      <input
                        type="checkbox"
                        checked={selectedNoteIds.has(String(note.id))}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleNoteSelected(note.id)}
                      />
                      <span className={cs.sidebarNoteIndex}>
                        {features.find((f) => f.id === note.featureId)?.properties.name} ({note.id})
                      </span>
                    </div>
                    <div className={cs.sidebarNoteStatus}>
                      {noteArea && <span className={cs.sidebarNoteArea}>{noteArea}</span>}
                      {isNoteSaved(note) ? (
                        <>
                          <B.Icon icon="tick" intent="success" size={16} />
                          <B.AnchorButton
                            minimal
                            icon={<B.Icon icon="share" size={12} />}
                            style={{minWidth: '16px', minHeight: '16px'}}
                            intent="primary"
                            small
                            title="Open saved note in new tab"
                            onClick={() => {
                              const noteLink = makeProjectDashboardUrl(
                                note.organizationId,
                                note.projectId,
                                'map',
                                [note.featureId],
                                makeNoteUrlFragment(note.id)
                              );
                              window.open(noteLink, '_blank');
                            }}
                          />
                        </>
                      ) : isNoteDismissed(note) ? (
                        <B.Icon icon="cross" intent="danger" size={16} />
                      ) : (
                        <B.Icon icon="blank" size={16} />
                      )}
                    </div>
                  </div>
                  {isCurrent ? (
                    <SelectedNoteReview
                      key={index}
                      noteWithEdits={noteWithEdits}
                      setNoteWithEdits={setNoteWithEdits}
                      onDismiss={onDismiss}
                      onSave={onSave}
                    />
                  ) : (
                    <div className={cs.sidebarNoteText}>
                      {note.text
                        ? note.text.substring(0, 90) + (note.text.length > 90 ? '...' : '')
                        : 'No text'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
